import React from "react"
import { useState } from "react"
import { useNavigate, Link } from "react-router-dom"
import { Card, Container, Row, Col, Button, Form, Alert } from "react-bootstrap"
import { FaShieldAlt, FaLock, FaEye, FaEyeSlash, FaUserCheck, FaGoogle, FaApple } from "react-icons/fa"
import { useAuth } from "../../context/AuthContext"

export default function AdminLogin() {
  const navigate = useNavigate()
  const { login } = useAuth()
  const [showAlert, setShowAlert] = useState({ show: false, variant: "", message: "" })
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [formData, setFormData] = useState({
    email: "",
    password: "",
    rememberMe: false,
  })

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!formData.email || !formData.password) {
      setShowAlert({ show: true, variant: "danger", message: "Please enter valid admin credentials." })
      setTimeout(() => setShowAlert({ show: false, variant: "", message: "" }), 3000)
      return
    }

    setLoading(true)
    try {
      const result = await login(formData.email, formData.password)
      
      if (result.success) {
        setShowAlert({
          show: true,
          variant: "success",
          message: "Admin Login Successful! Welcome to the admin dashboard.",
        })
        setTimeout(() => {
          navigate("/admin/dashboard")
        }, 1500)
      } else {
        setShowAlert({ show: true, variant: "danger", message: result.message })
      }
    } catch (error) {
      setShowAlert({ show: true, variant: "danger", message: "Login failed. Please try again." })
    } finally {
      setLoading(false)
      setTimeout(() => setShowAlert({ show: false, variant: "", message: "" }), 3000)
    }
  }

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }))
  }

  return (
    <div
      className="min-vh-100 d-flex align-items-center position-relative overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
      }}
    >
      {/* Animated Background Elements */}
      <div className="position-absolute w-100 h-100">
        <div
          className="position-absolute rounded-circle opacity-10"
          style={{
            width: "200px",
            height: "200px",
            background: "white",
            top: "10%",
            right: "15%",
            animation: "float 6s ease-in-out infinite",
          }}
        ></div>
        <div
          className="position-absolute rounded-circle opacity-10"
          style={{
            width: "150px",
            height: "150px",
            background: "white",
            bottom: "15%",
            left: "10%",
            animation: "float 8s ease-in-out infinite reverse",
          }}
        ></div>
      </div>

      <Container className="position-relative">
        <Row className="justify-content-center">
          <Col md={6} lg={5} xl={4}>
            {/* Header */}
            <div className="text-center mb-4">
              <div
                className="d-inline-flex align-items-center justify-content-center rounded-circle mb-4"
                style={{
                  width: "80px",
                  height: "80px",
                  background: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(20px)",
                  border: "1px solid rgba(255, 255, 255, 0.3)",
                }}
              >
                <FaShieldAlt size={32} className="text-white" />
              </div>
              <h2 className="h3 fw-bold text-white mb-2">Admin Portal</h2>
              <p className="text-white opacity-90">Secure access for Aidify administrators</p>
            </div>

            {/* Security Notice */}
            <div
              className="p-4 rounded-4 mb-4"
              style={{
                backgroundColor: "rgba(255, 255, 255, 0.1)",
                backdropFilter: "blur(20px)",
                border: "1px solid rgba(255, 255, 255, 0.2)",
              }}
            >
              <div className="d-flex align-items-center text-white">
                <FaShieldAlt className="me-3" size={24} />
                <div>
                  <h6 className="fw-bold mb-1">Secure Access</h6>
                  <small className="opacity-90">This portal is restricted to authorized personnel only</small>
                </div>
              </div>
            </div>

            {showAlert.show && (
              <Alert
                variant={showAlert.variant}
                className="mb-4 border-0 rounded-4"
                style={{
                  backdropFilter: "blur(20px)",
                  backgroundColor:
                    showAlert.variant === "success" ? "rgba(5, 150, 105, 0.9)" : "rgba(220, 38, 38, 0.9)",
                }}
              >
                <div className="text-white fw-medium">{showAlert.message}</div>
              </Alert>
            )}

            {/* Login Form */}
            <Card
              className="border-0 rounded-4 overflow-hidden"
              style={{
                backdropFilter: "blur(20px)",
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
              }}
            >
              <Card.Header className="bg-transparent border-0 p-4">
                <h5 className="fw-bold mb-1" style={{ color: "#1f2937" }}>
                  Administrator Sign In
                </h5>
                <p className="text-muted mb-0">Enter your admin credentials to access the management dashboard</p>
              </Card.Header>
              <Card.Body className="p-4">
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium text-dark mb-2">Email</Form.Label>
                    <div className="position-relative">
                      <div className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted">
                        <FaUserCheck size={16} />
                      </div>
                      <Form.Control
                        type="email"
                        value={formData.email}
                        onChange={(e) => handleInputChange("email", e.target.value)}
                        className="ps-5 py-3 border-0 rounded-3"
                        style={{
                          backgroundColor: "#f0fdfa",
                          fontSize: "16px",
                          transition: "all 0.3s ease",
                        }}
                        placeholder="Enter admin email"
                        required
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "#ccfbf1"
                          e.target.style.boxShadow = "0 0 0 3px rgba(8, 145, 178, 0.1)"
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "#f0fdfa"
                          e.target.style.boxShadow = "none"
                        }}
                      />
                    </div>
                  </Form.Group>

                  <Form.Group className="mb-4">
                    <Form.Label className="fw-medium text-dark mb-2">Password</Form.Label>
                    <div className="position-relative">
                      <div className="position-absolute top-50 start-0 translate-middle-y ms-3 text-muted">
                        <FaLock size={16} />
                      </div>
                      <Form.Control
                        type={showPassword ? "text" : "password"}
                        value={formData.password}
                        onChange={(e) => handleInputChange("password", e.target.value)}
                        className="ps-5 pe-5 py-3 border-0 rounded-3"
                        style={{
                          backgroundColor: "#f0fdfa",
                          fontSize: "16px",
                          transition: "all 0.3s ease",
                        }}
                        placeholder="Enter admin password"
                        required
                        onFocus={(e) => {
                          e.target.style.backgroundColor = "#ccfbf1"
                          e.target.style.boxShadow = "0 0 0 3px rgba(8, 145, 178, 0.1)"
                        }}
                        onBlur={(e) => {
                          e.target.style.backgroundColor = "#f0fdfa"
                          e.target.style.boxShadow = "none"
                        }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="btn btn-link position-absolute top-50 end-0 translate-middle-y me-3 text-muted p-0"
                        style={{ border: "none", background: "none" }}
                      >
                        {showPassword ? <FaEyeSlash size={16} /> : <FaEye size={16} />}
                      </button>
                    </div>
                  </Form.Group>

                  {/* <div className="d-flex justify-content-between align-items-center mb-4">
                     <Form.Check
                      type="checkbox"
                      id="remember"
                      label="Keep me signed in"
                      checked={formData.rememberMe}
                      onChange={(e) => handleInputChange("rememberMe", e.target.checked)}
                      className="text-muted"
                    /> 
                    <Link
                      to="/admin/forgot-password"
                      className="text-decoration-none fw-medium"
                      style={{ color: "#0891b2" }}
                    >
                      Forgot password?
                    </Link>
                  </div> */}

                  <Button
                    type="submit"
                    size="lg"
                    className="w-100 py-3 rounded-3 fw-bold border-0 mb-4"
                    style={{
                      background: "linear-gradient(135deg, #0891b2 0%, #0e7490 100%)",
                      boxShadow: "0 8px 25px rgba(8, 145, 178, 0.4)",
                      transform: "translateY(0)",
                      transition: "all 0.3s ease",
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.transform = "translateY(-2px)"
                      e.target.style.boxShadow = "0 12px 35px rgba(8, 145, 178, 0.5)"
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.transform = "translateY(0)"
                      e.target.style.boxShadow = "0 8px 25px rgba(8, 145, 178, 0.4)"
                    }}
                  >
                    <FaShieldAlt className="me-2" />
                    Sign In as Admin
                  </Button>

                 <div className="text-center">
                    <p className="text-muted mb-3">
                      Dont have account?{" "}
                      <Link to="/admin/register" className="text-decoration-none" style={{ color: "#0891b2" }}>
                        Register as an Admin
                      </Link>
                    </p>
                  </div>

                  <div className="text-center">
                    <p className="text-muted mb-3">
                      Need access?{" "}
                      <Link to="/contact" className="text-decoration-none" style={{ color: "#0891b2" }}>
                        Contact IT Support
                      </Link>
                    </p>
                  </div>
                </Form>
              </Card.Body>
            </Card>


            {/* Security Features */}
            <div className="text-center mt-4">
              <Row className="g-3">
                <Col xs={4}>
                  <div className="d-flex flex-column align-items-center">
                    <FaShieldAlt className="text-white opacity-75 mb-1" size={16} />
                    <small className="text-white opacity-75">Encrypted</small>
                  </div>
                </Col>
                <Col xs={4}>
                  <div className="d-flex flex-column align-items-center">
                    <FaLock className="text-white opacity-75 mb-1" size={16} />
                    <small className="text-white opacity-75">Secure</small>
                  </div>
                </Col>
                <Col xs={4}>
                  <div className="d-flex flex-column align-items-center">
                    <FaUserCheck className="text-white opacity-75 mb-1" size={16} />
                    <small className="text-white opacity-75">Verified</small>
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
        </Row>
      </Container>

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
      `}</style>
    </div>
  )
}
